import {
  type ForwardedRef,
  forwardRef,
  type ReactElement,
  type ReactNode,
  type RefAttributes,
  useRef,
} from 'react';

import useIntersectionObserver from '@/hooks/pure/useIntersectionObserver';
import { cn } from '@/lib/cn';

import { CenterFlex } from '../base';
import LazyLoader from '../Common/LazyLoader';
import Spinner from '../Common/Spinner';
import Transition from '../Common/Transition';
import { ToggleStage } from '../Stage/ToggleStage';
import { MainText } from '../Typography/NamedTypo';

type Props<T extends {}> = {
  isLoading?: boolean;
  data?: T[];
  render: (data: T, index: number) => ReactNode;
  placeholder?: {
    message?: string;
    node?: ReactNode;
  };
  loader?: ReactNode;
  isError?: boolean;
  errorDisplay?: {
    message?: string;
    node?: ReactNode;
  };
  lazyLoaderDelay?: number;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  disableAnimation?: boolean;
  className?: string;
};

const List = <T extends {}>(
  props: Props<T>,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  return (
    <ToggleStage
      className={cn(
        'flex flex-col overflow-auto scrollbar-hide',
        props.className,
      )}
      ref={ref}
      status={!props.isLoading}
      fallback={
        props.loader || (
          <LazyLoader
            show={props.isLoading}
            delay={props.lazyLoaderDelay}
            loader={
              <CenterFlex height="100%" f={1}>
                <Spinner />
              </CenterFlex>
            }
          />
        )
      }
    >
      {props.data && props.data.length > 0 ? (
        <>
          {props.data.map(props.render)}
          {props.fetchNextPage &&
            props.hasNextPage &&
            !props.isFetchingNextPage && (
              <NextPageLoader fetchNextPage={props.fetchNextPage} />
            )}
          <Transition status={!!props.isFetchingNextPage}>
            <CenterFlex style={{ height: 64 }}>
              <Spinner />
            </CenterFlex>
          </Transition>
        </>
      ) : props.isError ? (
        props.errorDisplay?.node ? (
          props.errorDisplay.node
        ) : (
          <CenterFlex className="h-full" f={1}>
            <MainText dim>
              {props.errorDisplay?.message || 'Error occurred'}
            </MainText>
          </CenterFlex>
        )
      ) : (
        props.placeholder?.node || (
          <CenterFlex className="h-full" f={1}>
            <MainText dim>{props.placeholder?.message || 'No data'}</MainText>
          </CenterFlex>
        )
      )}
    </ToggleStage>
  );
};

type ListComponentType = <T extends {}>(
  props: Props<T> & RefAttributes<HTMLDivElement>,
) => ReactElement | null;

type LoaderProps = {
  fetchNextPage: () => void;
};

const NextPageLoader = (props: LoaderProps) => {
  const loaderRef = useRef(null);

  const onIntersect = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      entry.isIntersecting ? props.fetchNextPage() : () => {};
    });
  };

  useIntersectionObserver({
    target: loaderRef,
    threshold: 0.7,
    onIntersect,
  });
  return (
    <div
      ref={loaderRef}
      style={{ width: '100%', height: '40px', flexShrink: 0 }}
    />
  );
};

export default forwardRef(List) as ListComponentType;
